import { useMemo } from 'react';

import type { BenefitCounter } from '../../../stores/schemas/userMe';
import { QueueStep1Preview } from '../popover/QueueStep1Preview';
import { QueueStep2Preview } from '../popover/QueueStep2Preview';
import type { OnboardingStep } from '../types';

// Ratings-queue tour — two static steps (no backend query, unlike the unrated
// tour). Both anchor the highlight to the whole queue table; the preview block
// passed via customContent is a shrunken copy of the table, so the centered
// popover reads as the Figma card. Body copy is verbatim from section-04
// (Step 2's leading space in the Figma source is stripped). primaryLabel drives
// the QueueOnboardingPopover's right button ("Next" then "Get started").
//
// `fastpass` is threaded from the launcher (which lives inside PermissionsProvider)
// into Step 2's live "Fastpasses" tile — the popover renders in a detached
// createRoot without provider context, so it can't read it itself.
export const useQueueOnboardingSteps = (fastpass?: BenefitCounter | null): OnboardingStep[] =>
  useMemo<OnboardingStep[]>(
    () => [
      {
        // Step 1 — voting (Figma 363:44723).
        // No `element` -> driver.js renders this as a dead-centre modal card
        // (matches the Figma centred onboarding steps).
        side: 'bottom',
        align: 'center',
        title: 'Vote for the projects you want rated next',
        body: 'You get votes each month. Use them on the unrated projects that matter to you. Projects with the most votes get rated next. Withdraw a vote anytime to use it elsewhere.',
        customContent: <QueueStep1Preview />,
        show: true,
        interactive: false,
        primaryLabel: 'Next',
      },
      {
        // Step 2 — Fastpass (Figma 363:46558).
        // No `element` -> driver.js renders this as a dead-centre modal card
        // (matches the Figma centred onboarding steps).
        side: 'bottom',
        align: 'center',
        title: 'Skip the queue with a Fastpass',
        body: 'Each Fastpass moves one project to the front of our review queue for a faster rating. Fastpasses may be subject to a fee depending on your subscription. Use them on the projects that matter most to your portfolio.',
        customContent: <QueueStep2Preview fastpass={fastpass} />,
        show: true,
        interactive: false,
        primaryLabel: 'Get started',
      },
    ],
    [fastpass]
  );
